import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { SupportMessage } from '@/lib/supportTickets';

interface SupportThreadProps {
  /** Which side is looking: 'user' on Contact Support, 'support' in the Admin Panel. */
  viewer: 'user' | 'support';
  messages: SupportMessage[];
  /** Resolves when the message is saved; the box clears on success. */
  onSend: (text: string) => Promise<boolean>;
  placeholder?: string;
}

/** A support conversation after the first message, and the box to add to it. */
export default function SupportThread({ viewer, messages, onSend, placeholder }: SupportThreadProps) {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setSending(true);
    try {
      if (await onSend(text)) setDraft('');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="support-thread">
      {messages.map(m => {
        const mine = m.author === viewer;
        return (
          <div
            key={m.id}
            data-testid="support-message"
            className={`rounded-lg p-2.5 text-sm whitespace-pre-wrap ${m.author === 'support' ? 'bg-primary/10' : 'bg-muted/50'} ${mine ? 'ml-6' : 'mr-6'}`}
          >
            <p className="text-[11px] font-semibold text-muted-foreground mb-1">
              {m.author === 'support' ? 'Support' : viewer === 'user' ? 'You' : 'Them'}
              {' · '}
              {formatDistanceToNow(new Date(m.created_at), { addSuffix: true })}
            </p>
            {m.body}
          </div>
        );
      })}
      <Textarea
        value={draft}
        onChange={e => setDraft(e.target.value)}
        placeholder={placeholder ?? 'Write a message…'}
        rows={3}
        maxLength={5000}
      />
      <div className="flex justify-end">
        <Button size="sm" className="h-8 text-xs gap-1" onClick={send} disabled={sending || !draft.trim()}>
          {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          Send
        </Button>
      </div>
    </div>
  );
}
